import { Link } from "react-router-dom";
import { useSeo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LOGO_APRATIM } from "@/data/content";

const TIMELINE = [
  {
    era: "The Foundation Years",
    brand: "Apratim Group / Apratim Builders",
    accent: "#5c1822",
    text: "The Jaybhaye family establishes Apratim Group in Chhatrapati Sambhaji Nagar (Aurangabad) — focused on honestly planned residential communities with clear titles and dependable construction.",
  },
  {
    era: "Delivered Landmarks",
    brand: "Apratim Radhai Estate · Apratim Park · Apratim Vastu · Apratim Pushp",
    accent: "#5c1822",
    text: "Flagship communities delivered and occupied. 900+ families, 15+ acres — a track record built household by household across the city.",
  },
  {
    era: "2022 — The Next Generation",
    brand: "Jaybhaye Realty Pvt. Ltd. incorporated",
    accent: "#b38b47",
    text: "Jaybhaye Realty Pvt. Ltd. is founded to carry the Apratim foundation into a new era — institutional standards, contemporary planning, and new markets.",
  },
  {
    era: "Today — The Pune Chapter",
    brand: "From Aurangabad to Pune",
    accent: "#b38b47",
    text: "The company is expanding across Pune's premier growth corridors — Baner, Bhugaon, Bavdhan, Hinjewadi, Kondhwa and Salisbury Park — in partnership with landowners and long-term stakeholders.",
  },
];

export default function LegacyPage() {
  useSeo({
    title: "Our Legacy | Apratim Group → Jaybhaye Realty Pvt. Ltd.",
    description:
      "Apratim Group / Apratim Builders built 900+ homes across 15+ acres in Chhatrapati Sambhaji Nagar over 15+ years. Jaybhaye Realty Pvt. Ltd. carries that legacy into Pune — new generation, new markets, new possibilities.",
    path: "/legacy",
    keywords: "Apratim Group, Apratim Builders, Apratim Group Aurangabad, Jaybhaye Realty legacy",
  });

  return (
    <div data-testid="legacy-page">
      <section className="py-24 bg-[#12281a] text-[#f7f5f0] border-b border-[#12281a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#b38b47] font-bold block mb-6">Our Legacy</span>
          <h1 className="font-monument text-4xl sm:text-6xl leading-[1.02] uppercase mb-8" data-testid="legacy-headline">
            Legacy. Experience. Foundation.
          </h1>
          <p className="text-xl text-[#f7f5f0]/75 font-light leading-snug max-w-3xl">
            Every Jaybhaye Realty development stands on fifteen years of Apratim Group groundwork — land understood,
            approvals honoured, families housed.
          </p>
        </div>
      </section>

      {/* Brand relationship */}
      <section className="py-24 bg-white border-b border-[#12281a]/10" data-testid="brand-relationship-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-stretch">
            <Reveal>
              <div className="border-l-4 border-[#5c1822] bg-[#f7f5f0] p-10 h-full" data-testid="apratim-brand-card">
                <img src={LOGO_APRATIM} alt="Apratim Group logo" className="h-16 object-contain mb-6 bg-white p-2" loading="lazy" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#5c1822] font-semibold block">The Foundation</span>
                <h2 className="text-2xl font-monument text-[#12281a] mt-2 mb-4">Apratim Group</h2>
                <p className="text-sm text-[#2c382f]/80 leading-relaxed mb-6">
                  Legacy. Experience. Foundation. Fifteen-plus years of delivered residential development in
                  Chhatrapati Sambhaji Nagar — 900+ homes across 15+ acres.
                </p>
                <div className="flex gap-6 text-xs text-[#12281a]/50 uppercase tracking-wider">
                  <span>15+ Years</span>
                  <span>·</span>
                  <span>900+ Families</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="border-l-4 border-[#b38b47] bg-[#12281a] text-[#f7f5f0] p-10 h-full" data-testid="jaybhaye-brand-card">
                <img src="/assets/jb-logo-full.png" alt="Jaybhaye Realty logo" className="h-16 w-auto object-contain mb-6" loading="lazy" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#b38b47] font-semibold block">The Next Generation</span>
                <h2 className="text-2xl font-monument mt-2 mb-4">Jaybhaye Realty Pvt. Ltd.</h2>
                <p className="text-sm text-[#f7f5f0]/70 leading-relaxed mb-6">
                  New Generation. New Markets. New Possibilities. Incorporated 2022 — scaling the Apratim foundation
                  into Pune and other Maharashtra markets.
                </p>
                <div className="flex gap-6 text-xs text-[#b38b47] uppercase tracking-wider font-semibold">
                  <span>Aurangabad → Pune</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="mb-16 text-center">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">The Journey</span>
              <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a]">From Foundation to Future</h2>
            </div>
          </Reveal>
          <div className="space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.era} delay={i * 100}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-t border-[#12281a]/15" data-testid={`timeline-item-${i}`}>
                  <div className="md:col-span-3">
                    <div className="chapter-num">{["I", "II", "III", "IV"][i]}</div>
                  </div>
                  <div className="md:col-span-9">
                    <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: t.accent }}>
                      {t.era}
                    </span>
                    <h3 className="text-xl font-monument text-[#12281a] mt-1 mb-3">{t.brand}</h3>
                    <p className="text-sm text-[#2c382f]/80 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <div className="text-center mt-16">
              <Link
                to="/contact"
                data-testid="legacy-contact-button"
                className="inline-block px-8 py-3.5 bg-[#12281a] hover:bg-[#1f422b] text-[#f7f5f0] text-xs uppercase tracking-widest font-semibold transition-colors"
              >
                Explore Our Journey With Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
